// src/pages/auth/AdminLoginPage.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowLeft,
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  AlertCircle,
  Loader2,
  Shield,
  Briefcase
} from 'lucide-react';

import useTranslation from '../../hooks/useTranslation';
import colors from '../../constants/colors';
import styles from '../../styles/AdminLoginPage.module.css';

import {
  adminLogin,
  selectAuthLoading,
  selectAuthError,
  clearErrors  // ✅ Now this will work
} from '../../store/slices/adminSlice';

const AdminLoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ======================================================
  // REDUX SELECTORS
  // ======================================================

  const isLoading = useSelector(selectAuthLoading);
  const authError = useSelector(selectAuthError);

  // ======================================================
  // LOCAL STATE
  // ======================================================

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');

  // ======================================================
  // CLEAR ERRORS ON MOUNT
  // ======================================================

  useEffect(() => {
    dispatch(clearErrors());

    // Clear local error when leaving the page
    return () => {
      dispatch(clearErrors());
    };
  }, [dispatch]);

  // ======================================================
  // TRANSLATIONS
  // ======================================================

  const { translatedText: errorTitle } =
    useTranslation('Error');

  const { translatedText: fillAllFields } =
    useTranslation('Please fill in all fields');

  const { translatedText: validEmail } =
    useTranslation('Please enter a valid email address');

  const { translatedText: invalidCredentials } =
    useTranslation('Invalid credentials');

  const { translatedText: appName } =
    useTranslation('Haitian Services Platform');

  const { translatedText: adminPortalSubtitle } =
    useTranslation('Administrator Portal');

  const { translatedText: adminLoginTitle } =
    useTranslation('Admin Login');

  const { translatedText: emailLabel } =
    useTranslation('Email Address');

  const { translatedText: emailPlaceholder } =
    useTranslation('Enter your admin email');

  const { translatedText: passwordLabel } =
    useTranslation('Password');

  const { translatedText: passwordPlaceholder } =
    useTranslation('Enter your password');

  const { translatedText: forgotPassword } =
    useTranslation('Forgot Password?');

  const { translatedText: signInAdmin } =
    useTranslation('Sign In as Admin');

  const { translatedText: goToProvider } =
    useTranslation('Go to Provider Login');

  // ======================================================
  // HANDLERS
  // ======================================================

  const handleLogin = async (e) => {
    e.preventDefault();

    // Prevent duplicate submissions
    if (isLoading) {
      return;
    }

    setLocalError('');
    dispatch(clearErrors());

    // ====================================================
    // VALIDATION
    // ====================================================

    const trimmedEmail = email.trim();

    if (!trimmedEmail || !password) {
      setLocalError(
        fillAllFields || 'Please fill in all fields'
      );
      return;
    }

    if (!trimmedEmail.includes('@')) {
      setLocalError(
        validEmail ||
        'Please enter a valid email address'
      );
      return;
    }

    // ====================================================
    // ADMIN LOGIN
    // ====================================================

    try {
      const result = await dispatch(
        adminLogin({
          email: trimmedEmail,
          password
        })
      ).unwrap();

      // ==================================================
      // SUCCESS
      // ==================================================

      if (result?.success) {
        navigate('/admin/overview', {
          replace: true
        });
      }
    } catch (error) {
      // ==================================================
      // LOGIN ERROR
      // ==================================================

      console.error('Admin login error:', error);

      // The Redux slice normally provides authError.
      // This fallback handles unexpected errors.
      if (!authError) {
        const message =
          error?.message ||
          invalidCredentials ||
          'Invalid credentials';

        setLocalError(message);
      }
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const togglePasswordVisibility = () => {
    if (isLoading) {
      return;
    }

    setShowPassword((previous) => !previous);
  };

  const handleForgotPassword = () => {
    if (isLoading) {
      return;
    }

    navigate('/admin/forgot-password');
  };

  // ======================================================
  // ERROR
  // ======================================================

  const displayError = localError || authError;

  // ======================================================
  // RENDER
  // ======================================================

  return (
    <div className={styles.adminLoginContainer}>
      <div className={styles.adminLoginWrapper}>
        <div className={styles.adminLoginCard}>

          {/* ==================================================
              BACK BUTTON
          ================================================== */}

          <button
            type="button"
            className={styles.backButton}
            onClick={handleBack}
            disabled={isLoading}
            aria-label="Go back"
          >
            <ArrowLeft
              size={24}
              color={colors.textPrimary}
            />
          </button>

          {/* ==================================================
              LOGO SECTION
          ================================================== */}

          <div className={styles.logoSection}>
            <div className={styles.logoContainer}>

              <div className={styles.logoBox}>
                <img
                  src="/logo/logo_hsm.png"
                  alt="Haitian Services Marketplace"
                  className={styles.logoImage}
                />
              </div>

              <h1 className={styles.logoText}>
                {appName}
              </h1>

            </div>

            <p className={styles.subtitle}>
              {adminPortalSubtitle}
            </p>
          </div>

          {/* ==================================================
              ADMIN BADGE
          ================================================== */}

          <div
            className={styles.adminBadge}
            style={{
              borderColor: '#7C3AED'
            }}
          >
            <Shield size={24} />

            <span
              className={styles.adminBadgeText}
              style={{
                color: '#7C3AED'
              }}
            >
              {adminLoginTitle}
            </span>
          </div>

          {/* ==================================================
              PROVIDER LOGIN LINK
          ================================================== */}

          <div className={styles.providerLinkContainer}>
            <Link
              to="/login"
              className={styles.providerLink}
            >
              <Briefcase size={16} />

              <span>
                {goToProvider}
              </span>

              <ArrowRight size={14} />
            </Link>
          </div>

          {/* ==================================================
              ERROR MESSAGE
          ================================================== */}

          {displayError && (
            <div
              className={styles.errorMessage}
              role="alert"
              aria-live="assertive"
            >
              <AlertCircle size={18} />

              <span className={styles.errorText}>
                {displayError}
              </span>
            </div>
          )}

          {/* ==================================================
              LOGIN FORM
          ================================================== */}

          <form onSubmit={handleLogin}>

            {/* ==================================================
                EMAIL
            ================================================== */}

            <div className={styles.inputGroup}>

              <label
                htmlFor="admin-email"
                className={styles.formLabel}
              >
                {emailLabel}
              </label>

              <div className={styles.inputWrapper}>

                <Mail
                  size={20}
                  className={styles.inputIcon}
                  color={colors.gray400}
                />

                <input
                  id="admin-email"
                  type="email"
                  className={styles.formControl}
                  placeholder={emailPlaceholder}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);

                    if (localError) {
                      setLocalError('');
                    }
                  }}
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="email"
                  spellCheck="false"
                  disabled={isLoading}
                  required
                />

              </div>
            </div>

            {/* ==================================================
                PASSWORD
            ================================================== */}

            <div className={styles.inputGroup}>

              <label
                htmlFor="admin-password"
                className={styles.formLabel}
              >
                {passwordLabel}
              </label>

              <div className={styles.inputWrapper}>

                <Lock
                  size={20}
                  className={styles.inputIcon}
                  color={colors.gray400}
                />

                <input
                  id="admin-password"
                  type={
                    showPassword
                      ? 'text'
                      : 'password'
                  }
                  className={`${styles.formControl} ${styles.formControlWithEye}`}
                  placeholder={passwordPlaceholder}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);

                    if (localError) {
                      setLocalError('');
                    }
                  }}
                  autoComplete="current-password"
                  disabled={isLoading}
                  required
                />

                <button
                  type="button"
                  className={styles.eyeButton}
                  onClick={togglePasswordVisibility}
                  disabled={isLoading}
                  aria-label={
                    showPassword
                      ? 'Hide password'
                      : 'Show password'
                  }
                >
                  {showPassword ? (
                    <EyeOff
                      size={20}
                      color={colors.gray400}
                    />
                  ) : (
                    <Eye
                      size={20}
                      color={colors.gray400}
                    />
                  )}
                </button>

              </div>
            </div>

            {/* ==================================================
                FORGOT PASSWORD
            ================================================== */}

            <div
              className={
                styles.forgotPasswordContainer
              }
            >
              <button
                type="button"
                className={styles.forgotPassword}
                disabled={isLoading}
                onClick={handleForgotPassword}
              >
                {forgotPassword}
              </button>
            </div>

            {/* ==================================================
                LOGIN BUTTON
            ================================================== */}

            <button
              type="submit"
              className={styles.adminLoginButton}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2
                    size={20}
                    className={styles.spinner}
                  />

                  <span>
                    Signing in...
                  </span>
                </>
              ) : (
                <>
                  <span>
                    {signInAdmin}
                  </span>

                  <ArrowRight size={20} />
                </>
              )}
            </button>

            {/* ==================================================
                SECURITY NOTICE
            ================================================== */}

            <div className={styles.securityNotice}>

              <Shield
                size={16}
                color={colors.gray500}
              />

              <span
                className={
                  styles.securityNoticeText
                }
              >
                This area is restricted to authorized
                administrators only.
              </span>

            </div>

          </form>

          {/* ==================================================
              ADMIN REGISTER
          ================================================== */}

          <div
            className={
              styles.registerLinkContainer
            }
          >
            <span className={styles.registerText}>
              Need an admin account?
            </span>

            <Link
              to="/admin/register"
              className={styles.registerLink}
            >
              Register as Admin
            </Link>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;